// Subscriptions Service (5.3 Subscription/Order Module)
// Manages CRUD operations for subscriptions

import { BaseService } from '@/chassis/data/services/base-service';
import { ValidationRules } from '@/chassis/data/services/base-form-service';
import { createNormalizer, FieldTransforms } from '@/chassis/data/services/normalizer-utils';
import { mapPaginatedResponse } from '@/chassis/core/common-helpers/pagination';
import { createSimpleFormService } from '@/chassis/data/services/simple-form-service';
import { findCoveringSubscription } from '../plan-scope';

export interface SubscriptionRecord {
    id: string;
    supplierId: string;
    userId?: string;
    planId: string;
    status: string;
    billingCycle: string;
    startDate: string;
    endDate?: string;
    nextBillingDate?: string;
    amount?: number;
    currency?: string;
    trialPeriod?: string;
    discounts?: string;
    addOns?: string;
    metadata?: string;
    lastPaymentStatus?: string;
    autoRenewal: boolean;
    active: boolean;
    isActive?: boolean;
    expiresAt?: string;
    gatewaySubscriptionId?: string;
    /** Abrangencia gravada na compra: NATIONAL (ou ausente) ou AGENCY. */
    planScope?: string;
    /** Orgao do plano quando planScope=AGENCY. */
    planAgencyId?: string;
    /** Valor pago (BRL); na troca para o nacional, so o tempo que faltava do plano de orgao. */
    amountPaid?: number;
    /** Preco cheio do plano na compra (BRL). */
    listPrice?: number;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    createdByName?: string;
    createdByUser?: string;
    updatedBy?: string;
    updatedByName?: string;
    updatedByUser?: string;
}

export interface SubscriptionFilters {
    searchTerm?: string;
    supplierId?: string;
    userId?: string;
    planId?: string;
    status?: string;
    billingCycle?: string;
    autoRenewal?: string;
    active?: boolean;
    page?: number;
    limit?: number;
}

export interface SubscriptionFormData extends Partial<SubscriptionRecord> {
    supplierId: string;
    planId: string;
    status: string;
    billingCycle: string;
    startDate: string;
    autoRenewal: boolean;
    active: boolean;
}

const stringifyIfObject = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const toNumberOrUndefined = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? undefined : numberValue;
};

export const normalizeSubscription = createNormalizer<SubscriptionRecord>({
    idFields: ['supplierId', 'planId'],
    fields: [
        'supplierId',
        'userId',
        'planId',
        'status',
        'billingCycle',
        'startDate',
        'endDate',
        'nextBillingDate',
        { target: 'amount', transform: toNumberOrUndefined },
        'currency',
        'trialPeriod',
        'discounts',
        'addOns',
        'metadata',
        { target: 'lastPaymentStatus', transform: FieldTransforms.identity },
        { target: 'planScope', transform: FieldTransforms.identity },
        { target: 'planAgencyId', transform: FieldTransforms.identity },
        { target: 'amountPaid', transform: toNumberOrUndefined },
        { target: 'listPrice', transform: toNumberOrUndefined },
        { target: 'autoRenewal', transform: FieldTransforms.toBoolean },
        { target: 'active', transform: FieldTransforms.toBoolean },
        { target: 'createdAt', transform: FieldTransforms.identity },
        { target: 'updatedAt', transform: FieldTransforms.identity },
        { target: 'createdBy', transform: FieldTransforms.identity },
        { target: 'createdByName', transform: FieldTransforms.identity },
        { target: 'createdByUser', transform: FieldTransforms.identity },
        { target: 'updatedBy', transform: FieldTransforms.identity },
        { target: 'updatedByName', transform: FieldTransforms.identity },
        { target: 'updatedByUser', transform: FieldTransforms.identity },
    ],
    customize: (record, raw) => {
        const rawSupplier = (raw?.supplierId as string | undefined) ?? (raw?.supplier_id as string | undefined) ?? (raw?.userId as string | undefined) ?? (raw?.user_id as string | undefined);
        if ((!record.supplierId || record.supplierId.length === 0) && typeof rawSupplier === 'string' && rawSupplier.trim()) {
            record.supplierId = rawSupplier.trim();
        }
        record.autoRenewal = raw?.autoRenewal === true;
        const resolvedActive = raw?.active ?? record.active;
        record.active = resolvedActive === undefined ? true : Boolean(resolvedActive);
        record.discounts = stringifyIfObject(raw?.discounts);
        record.addOns = stringifyIfObject(raw?.addOns);
        record.metadata = stringifyIfObject(raw?.metadata);
    },
});

/**
 * Seleciona a subscription ativa de uma lista de subscriptions.
 * Reutilizável por qualquer componente que precise verificar se o fornecedor tem plano ativo.
 */
export function pickActiveSubscription(items: SubscriptionRecord[]): SubscriptionRecord | null {
    return (
        items.find((sub) => {
            if (sub.active === false) return false;
            const status = String(sub.status ?? '').toUpperCase();
            return status !== 'CANCELLED' && status !== 'INACTIVE';
        }) ?? null
    );
}

class SubscriptionsService extends BaseService<SubscriptionRecord, SubscriptionFormData, Partial<SubscriptionFormData>, SubscriptionFilters> {
    constructor() {
        super({
            basePath: '/subscriptions',
            normalizeRecord: normalizeSubscription,
            mapPaginated: (raw) => mapPaginatedResponse(raw, normalizeSubscription),
        });
    }

    /**
     * Verifica se um fornecedor possui subscription ativa.
     * Usa ensureResourcePrefix para garantir formato correto do supplierId.
     * Retorna a subscription ativa ou null.
     */
    async findActiveBySupplier(rawSupplierId: string | null | undefined): Promise<SubscriptionRecord | null> {
        if (!rawSupplierId) return null;

        const { DataSanitizationUtils } = await import('@/chassis/core/utils');
        const supplierId = DataSanitizationUtils.ensureResourcePrefix('SUPPLIER', rawSupplierId);
        if (!supplierId) return null;

        try {
            const result = await this.list({ supplierId, active: true } as any);
            return pickActiveSubscription(result?.items ?? []);
        } catch (error) {
            console.warn('[SubscriptionsService] findActiveBySupplier failed for:', supplierId, error);
            return null;
        }
    }

    /**
     * Assinatura vigente que libera as licitacoes do orgao informado (planos por orgao):
     * plano nacional libera qualquer orgao; plano de orgao, so o proprio. Mesma regra do gate
     * de envio da proposta no backend.
     */
    async findCoveringBySupplier(rawSupplierId: string | null | undefined, agencyId: string | null | undefined): Promise<SubscriptionRecord | null> {
        if (!rawSupplierId) return null;

        const { DataSanitizationUtils } = await import('@/chassis/core/utils');
        const supplierId = DataSanitizationUtils.ensureResourcePrefix('SUPPLIER', rawSupplierId);
        if (!supplierId) return null;

        try {
            const result = await this.list({ supplierId, active: true } as any);
            return findCoveringSubscription(result?.items ?? [], agencyId);
        } catch (error) {
            console.warn('[SubscriptionsService] findCoveringBySupplier failed for:', supplierId, error);
            return null;
        }
    }
}

export const subscriptionsService = new SubscriptionsService();

// Simple form service - no complex transformations needed
export const createSubscriptionsFormService = () =>
    createSimpleFormService<SubscriptionRecord, SubscriptionFormData>({
        basePath: '/subscriptions',
        initialData: {
            supplierId: '',
            userId: '',
            planId: '',
            status: '',
            billingCycle: '',
            startDate: '',
            endDate: '',
            nextBillingDate: '',
            amount: undefined,
            currency: '',
            trialPeriod: '',
            discounts: '',
            addOns: '',
            metadata: '',
            autoRenewal: false,
            active: true,
        },
        validationRules: {
            supplierId: [ValidationRules.required('Fornecedor é obrigatório')],
            planId: [ValidationRules.required('Plano é obrigatório')],
            status: [ValidationRules.required('Status é obrigatório')],
            billingCycle: [ValidationRules.required('Ciclo de cobrança é obrigatório')],
            startDate: [ValidationRules.required('Data de início é obrigatória')],
        },
        normalizeRecord: normalizeSubscription,
    });
